import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  PaginationItem,
  Menu,
  MenuItem,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import styled from "@emotion/styled";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import NoDataFound from "../../Components/Masters/NoDataFound";
import { getMonthName } from "../../utils/getMonthName";
import PaginationBox from "../Masters/Pagination";
import { truncateNumber } from "../../utils/common";
import { formatDate, formatDateMonth } from "../../utils/formatDate";
import { formatUnitOfProductSoldCell } from "../../utils/unitOfProductSoldDisplay";
import EllipsisTooltip from "./EllipsisTooltip";

/** Default Action column width (px) when `tableLayoutAuto` reserves space for the row menu. */
const LIST_TABLE_ACTION_WIDTH_PX = 56;

const isDateLikeHeaderKey = (key) =>
  key === "createdAt" ||
  key === "dateOfTravel" ||
  key === "date" ||
  key === "dateOfSale" ||
  key === "purchaseDate" ||
  key === "soldProductDate" ;

/** Preserve 0; only null, undefined, and "" become "N/A". */
const displayTableCellValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  return value;
};

const getCellDisplayValue = (row, header, getMonth) => {
  if (
    header.key === "createdAt" ||
    header.key === "dateOfTravel" ||
    header.key === "date" ||
    header.key === "dateOfSale" ||
    header.key === "purchaseDate" ||
    header.key === "soldProductDate"
  ) {
    return formatDateMonth(row[header.key]);
  }
  if (header.key === "monthYear" || header.key === "consumptionPeriod") {
    return `${getMonthName(
      row?.data ? row?.data?.[header.key]?.month : row?.[header.key]?.month
    )} ${
      row?.data ? row?.data?.[header.key]?.year : row?.[header.key]?.year
    }`;
  }
  if (header.key === "month") return getMonth(row[header.key]);
  if (header.key === "unitOfProductSold") return formatUnitOfProductSoldCell(row);
  if (
    header.key === "quantityOfWasteManaged" &&
    row.wasteManagementMethod === "Landfilling"
  ) {
    return "N/A";
  }
  if (header.key === "assetSize") {
    const a = row.assetSize;
    if (a && typeof a === "object" && a !== null) {
      const val = a.value !== undefined && a.value !== null ? a.value : "";
      return displayTableCellValue(val);
    }
    return displayTableCellValue(a);
  }
  if (header.subKey) {
    return displayTableCellValue(row[header.key]?.[header.subKey]);
  }
  if (header.key === "test") {
    return `${row[header.key]?.value} ${row[header.key]?.unit}`;
  }
  if (header.key === "productName") return row[header.key] || "N/A";
  if (header.field) {
    return `${row[header.key]?.[header.field] || "N/A"}`;
  }
  if (header.key === "freightLoad" || header.key === "distanceCovered") {
    return `${truncateNumber(row[header.key]?.value)}`;
  }
  if (
    header.key === "quantityOfWasteGenerated" ||
    header.key === "quantityOfWasteManaged" ||
    header.key === "quantityProduced"
  ) {
    return (
      truncateNumber(row[header.key]?.value || 0) +
      " " +
      row[header.key]?.unit
    );
  }
  if (header.truncate) return truncateNumber(row[header.key]);
  return displayTableCellValue(row[header.key]);
};

const ViewTable = ({
  title,
  headers,
  data,
  onSort,
  sortConfig,
  page,
  count,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  editIcon,
  totalPages,
  tableContainerRef,
  isShortContent,
  /** When true: table fills width, data columns share space minus Action column; compact padding/fonts; no horizontal overflow. */
  tableLayoutAuto = false,
  /** Width of the Action column in px (default from listTableGrid). Increase when data columns use fluidWeight. */
  actionColumnWidthPx = LIST_TABLE_ACTION_WIDTH_PX,
  actionColumnWidthPercent = null,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
    "&.Mui-selected": {
      backgroundColor: "green",
      color: "#fff",
      fontWeight: "bold",
    },
    "&:hover": {
      backgroundColor: "#ededed",
    },
  }));

  const handlePageChange = (event, newPage) => {
    onPageChange(newPage);
  };

  const getMonth = (dateStr) => {
    const date = new Date(dateStr);

    return date.toLocaleString("default", { month: "short", year: "numeric" });
  };

  /** Width reserved for the Action column (px). */
  const actionW =
    Number(actionColumnWidthPx) > 0 ? Number(actionColumnWidthPx) : LIST_TABLE_ACTION_WIDTH_PX;

  const usePercentAction =
    !tableLayoutAuto &&
    actionColumnWidthPercent != null &&
    Number(actionColumnWidthPercent) > 0;
  const actionCellWidth = usePercentAction
    ? `${Number(actionColumnWidthPercent)}%`
    : actionW;
  const actionCellDimensions = usePercentAction
    ? {
        width: actionCellWidth,
        minWidth: actionCellWidth,
        maxWidth: actionCellWidth,
      }
    : { width: actionW, minWidth: actionW, maxWidth: actionW };

  const totalFluidWeight =
    tableLayoutAuto && Array.isArray(headers) && headers.length > 0
      ? headers.reduce(
          (sum, h) =>
            sum +
            (typeof h.fluidWeight === "number" && h.fluidWeight > 0 ? h.fluidWeight : 1),
          0,
        )
      : 0;

  const useFluidWeights =
    tableLayoutAuto &&
    Array.isArray(headers) &&
    headers.some((h) => typeof h.fluidWeight === "number" && h.fluidWeight > 0);

  /** Per-column share of (100% - action column); equal split unless headers use fluidWeight. */
  const fluidColumnWidth = (header) => {
    if (!tableLayoutAuto || !Array.isArray(headers) || headers.length === 0) return null;
    if (useFluidWeights) {
      const w =
        typeof header.fluidWeight === "number" && header.fluidWeight > 0
          ? header.fluidWeight
          : 1;
      return `calc((100% - ${actionW}px) * ${w} / ${totalFluidWeight})`;
    }
    return `calc((100% - ${actionW}px) / ${headers.length})`;
  };

  return (
    <>
      <TableContainer
        component={Paper}
        ref={tableContainerRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          borderRadius: "8px",
          marginBottom: "1.5rem",
          ...(tableLayoutAuto
            ? {
                border: "1px solid #ddd",
                backgroundColor: "#fff",
                boxShadow: "none",
                overflow: "hidden",
              }
            : {
                borderBottom: "1px solid #ddd",
                borderTop: "1px solid #ddd",
              }),
        }}
      >
        <Box
          sx={{
            padding: "1rem 0.75rem",
            ...(tableLayoutAuto ? { borderBottom: "1px solid #ddd" } : {}),
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              fontSize: tableLayoutAuto ? "16px" : "20px",
              textAlign: "left",
              color: "#000",
            }}
          >
            {title ? title : "Table"}
          </Box>
        </Box>
        <Table
          sx={{
            width: "100%",
            tableLayout: "fixed",
            ...(tableLayoutAuto
              ? {
                  minWidth: 0,
                  borderBottom: "1px solid #ddd",
                }
              : {}),
          }}
        >
          <TableHead
            sx={{
              backgroundColor: tableLayoutAuto ? "#fff" : "#fff",
            }}
          >
            <TableRow sx={{ width: "100%" }}>
              {headers.map((header) => {
                const fluidW = fluidColumnWidth(header);
                return (
                <TableCell
                  key={header.key}
                  sx={{
                    borderRight: "1px solid #ddd",
                    borderTop: tableLayoutAuto ? "none" : "1px solid #ddd",
                    padding: tableLayoutAuto ? "10px 8px" : "10px",
                    boxSizing: "border-box",
                    ...(tableLayoutAuto && fluidW
                      ? {
                          width: fluidW,
                          minWidth: 0,
                          wordBreak: "break-word",
                          verticalAlign: "middle",
                        }
                      : {
                          width: header.width,
                          ...(header.key === "facilityName"
                            ? { minWidth: header.width || "10rem" }
                            : {}),
                        }),
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{
                      gap: 0.75,
                      minWidth: 0,
                      width: "100%",
                      ...(tableLayoutAuto
                        ? { flexWrap: "nowrap" }
                        : { alignItems: "flex-start" }),
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: tableLayoutAuto ? 700 : "600",
                        flex: "1 1 0%",
                        fontSize: "16px",
                        minWidth: 0,
                        lineHeight: 1.35,
                        color: "#000",
                        ...(header.key === "facilityName"
                          ? {
                              whiteSpace: "normal",
                              wordBreak: "normal",
                              overflowWrap: "normal",
                            }
                          : {
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }),
                        textAlign: "left",
                      }}
                    >
                      {header.label}
                    </Typography>
                    {header.sortable &&
                      (tableLayoutAuto ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            flexShrink: 0,
                            alignItems: "center",
                            justifyContent: "center",
                            alignSelf: "center",
                            gap: 0,
                            ml: 0.25,
                          }}
                        >
                          <IconButton
                            size="small"
                            aria-label={`Sort ${header.label} ascending`}
                            onClick={() => onSort(header.key, "asc")}
                            sx={{ p: "1px", minWidth: 28, height: 18 }}
                          >
                            <ExpandLess
                              sx={{
                                fontSize: 18,
                                lineHeight: 1,
                                display: "block",
                                color:
                                  sortConfig.key === header.key &&
                                  sortConfig.direction === "asc"
                                    ? "#1565c0"
                                    : "#1a1a1a",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Sort ${header.label} descending`}
                            onClick={() => onSort(header.key, "desc")}
                            sx={{ p: "1px", minWidth: 28, height: 18, mt: "-6px" }}
                          >
                            <ExpandMore
                              sx={{
                                fontSize: 18,
                                lineHeight: 1,
                                display: "block",
                                color:
                                  sortConfig.key === header.key &&
                                  sortConfig.direction === "desc"
                                    ? "#1565c0"
                                    : "#1a1a1a",
                              }}
                            />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          sx={{ flexShrink: 0, ml: 0.25 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => onSort(header.key, "asc")}
                          >
                            <ExpandLess
                              sx={{
                                fontSize: "20px",
                                margin: "-10px",
                                fontWeight: "700",
                                color:
                                  sortConfig.key === header.key &&
                                  sortConfig.direction === "asc"
                                    ? "blue"
                                    : "black",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onSort(header.key, "desc")}
                          >
                            <ExpandMore
                              sx={{
                                fontSize: "20px",
                                fontWeight: "700",
                                margin: "-10px",
                                color:
                                  sortConfig.key === header.key &&
                                  sortConfig.direction === "desc"
                                    ? "blue"
                                    : "black",
                              }}
                            />
                          </IconButton>
                        </Box>
                      ))}
                  </Box>
                </TableCell>
              );
              })}
              <TableCell
                key={"actions"}
                sx={{
                  borderTop: tableLayoutAuto ? "none" : "1px solid #ddd",
                  borderRight: "1px solid #ddd",
                  padding: tableLayoutAuto ? "10px 8px" : "4px 2px",
                  fontSize: "16px",
                  fontWeight: tableLayoutAuto ? 700 : "600",
                  color: tableLayoutAuto ? "#000" : undefined,
                  ...actionCellDimensions,
                  textAlign: "center",
                  boxSizing: "border-box",
                  verticalAlign: "middle",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.length === 0 ? (
              <TableRow sx={{ width: "100%" }}>
                <TableCell
                  colSpan={headers.length + 1}
                  sx={{
                    textAlign: "center",
                    padding: tableLayoutAuto ? "48px 16px" : "20px",
                    verticalAlign: "middle",
                    borderRight: tableLayoutAuto ? "1px solid #ddd" : undefined,
                    ...(tableLayoutAuto ? { minHeight: "220px" } : {}),
                  }}
                >
                  <NoDataFound />
                </TableCell>
                {/* <NoDataFound /> */}
              </TableRow>
            ) : (
              data?.map((row) => (
                <TableRow key={row._id ?? row.id} size="small">
                  {headers.map((header) => {
                    const fluidW = fluidColumnWidth(header);
                    const dateLike = isDateLikeHeaderKey(header.key);
                    const cellValue = getCellDisplayValue(row, header, getMonth);
                    return (
                    <TableCell
                      key={header?.key}
                      size="small"
                      sx={{
                        borderRight: "1px solid #ddd",
                        padding: tableLayoutAuto
                          ? dateLike
                            ? "2px 4px"
                            : "3px 6px"
                          : "8px 12px",
                        fontSize: tableLayoutAuto ? "14px" : "14px",
                        lineHeight: tableLayoutAuto ? 1.15 : 1.2,
                        boxSizing: "border-box",
                        ...(tableLayoutAuto && fluidW
                          ? {
                              width: fluidW,
                              minWidth: 0,
                              textAlign: "left",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: dateLike ? "nowrap" : "normal",
                              wordBreak: dateLike ? "normal" : "break-word",
                              verticalAlign: "middle",
                            }
                          : {
                              width: header.width,
                              textAlign:
                                header.key === "createdAt" ? "center" : "left",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }),
                      }}
                    >
                      <EllipsisTooltip
                        title={cellValue}
                        sx={{
                          fontSize: tableLayoutAuto
                            ? dateLike
                              ? "13px"
                              : "14px"
                            : "14px",
                          ...(tableLayoutAuto
                            ? {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: dateLike ? "nowrap" : "normal",
                                wordBreak: dateLike ? "normal" : "break-word",
                                textAlign: "left",
                                color: "#000",
                                lineHeight: dateLike ? 1.2 : 1.25,
                                display: "block",
                              }
                            : {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                              }),
                        }}
                      >
                        {cellValue}
                      </EllipsisTooltip>
                    </TableCell>
                  );
                  })}
                  <TableCell
                    sx={{
                      textAlign: "center",
                      padding: tableLayoutAuto ? "3px 4px" : "0px 6px",
                      fontSize: tableLayoutAuto ? "14px" : "14px",
                      lineHeight: "1",
                      ...actionCellDimensions,
                      boxSizing: "border-box",
                      borderRight: "1px solid #ddd",
                      verticalAlign: "middle",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, row)}
                      sx={tableLayoutAuto ? { p: "2px" } : undefined}
                    >
                      <MoreHorizIcon
                        sx={{
                          fontWeight: "bold",
                          color: "black",
                          ...(tableLayoutAuto ? { fontSize: 20 } : {}),
                        }}
                      />
                    </IconButton>
                    </Box>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        sx: {
                          width: "120px",
                          border: "1px solid #ddd",
                          marginLeft: "-47px",
                          boxShadow: "none",
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          onView(selectedRow);
                        }}
                        sx={{ fontSize: "14px" }}
                      >
                        <AssignmentIcon
                          sx={{ marginRight: "8px", fontSize: "large" }}
                        />{" "}
                        View Logs
                      </MenuItem>
                      {editIcon !== "disable" && typeof onEdit === "function" ? (
                        <MenuItem
                          onClick={() => {
                            handleMenuClose();
                            onEdit(selectedRow);
                          }}
                          sx={{ fontSize: "14px" }}
                        >
                          <EditIcon
                            sx={{ marginRight: "8px", fontSize: "large" }}
                          />{" "}
                          Edit
                        </MenuItem>
                      ) : null}

                      {editIcon !== "disable" && typeof onDelete === "function" ? (
                        <MenuItem
                          onClick={() => {
                            handleMenuClose();
                            onDelete(selectedRow);
                          }}
                          sx={{ fontSize: "14px" }}
                        >
                          <DeleteIcon
                            sx={{ marginRight: "8px", fontSize: "large" }}
                          />{" "}
                          Delete
                        </MenuItem>
                      ) : (
                        ""
                      )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <PaginationBox
          {...{
            totalPages,
            page,
            handleChangePage: handlePageChange,
            StyledPaginationItem,
            isShortContent,
          }}
        />
      )}
    </>
  );
};

export default ViewTable;
